/**
 * Thematique Filter Module
 * Filtrage AJAX des actualites et realisations par thematique
 *
 * Fonctionnalites :
 * - Mode multi-selection avec badges (actualites)
 * - Mode simple avec filtrage au changement (realisations/FAQ)
 * - Accessibilite RGAA avec aria-live
 */

export default class ThematiqueFilter {
  constructor() {
    this.selects = document.querySelectorAll('[id$="-thematique-select"]');

    if (this.selects.length > 0) {
      this.init();
    }
  }

  init() {
    this.selects.forEach((select) => {
      const isMulti = select.dataset.multi === "true";

      if (isMulti) {
        this.initMultiMode(select);
      } else {
        this.initSimpleMode(select);
      }
    });
  }

  // ─── MODE SIMPLE (realisations/FAQ) ───────────────────────────

  initSimpleMode(select) {
    select.addEventListener("change", (e) => this.handleSimpleFilterChange(e));
    this.updateSelectState(select);
    this.restoreSimpleSelection(select);
  }

  updateSelectState(select) {
    if (select.value) {
      select.classList.add("has-value");
    } else {
      select.classList.remove("has-value");
    }
  }

  async handleSimpleFilterChange(event) {
    const select = event.target;
    const thematiqueId = select.value;
    const pageSection = select.dataset.pageSection;

    this.updateSelectState(select);

    if (thematiqueId) {
      sessionStorage.setItem("thematique_filter_" + pageSection, thematiqueId);
    } else {
      sessionStorage.removeItem("thematique_filter_" + pageSection);
    }

    await this.fetchResults(pageSection, thematiqueId, select);
  }

  restoreSimpleSelection(select) {
    const currentUrl = new URL(window.location.href);
    const urlThematique = currentUrl.searchParams.get("thematique");
    const pageSection = select.dataset.pageSection;

    if (urlThematique) {
      const optionExists = Array.from(select.options).some(
        (opt) => opt.value === urlThematique,
      );
      if (optionExists) {
        select.value = urlThematique;
        sessionStorage.setItem(
          "thematique_filter_" + pageSection,
          urlThematique,
        );
      }
    } else {
      select.value = "";
      sessionStorage.removeItem("thematique_filter_" + pageSection);
    }

    this.updateSelectState(select);
  }

  // ─── MODE MULTI-SELECTION (actualites) ────────────────────────

  initMultiMode(select) {
    const pageSection = select.dataset.pageSection;
    this.selectedThematiques = new Map(); // id -> name

    const searchBtn = document.getElementById(
      pageSection + "-thematique-search",
    );
    const badgesContainer = document.getElementById(
      pageSection + "-thematique-badges",
    );

    // Au changement du select : ajouter un badge ou tout vider
    // Note : on ne remet PAS le select à vide après ajout, sinon
    // l'utilisateur ne peut plus distinguer "je choisis Toutes" de "reset auto"
    select.addEventListener("change", () => {
      const id = select.value;

      if (!id) {
        // "Toutes les thematiques..." sélectionné explicitement → vider tous les badges
        this.selectedThematiques.clear();
        this.renderBadges(badgesContainer, pageSection);
        return;
      }

      if (!this.selectedThematiques.has(id)) {
        const name = select.options[select.selectedIndex].text;
        this.selectedThematiques.set(id, name);
        this.renderBadges(badgesContainer, pageSection);
      }
    });

    // Clic sur RECHERCHER
    if (searchBtn) {
      searchBtn.addEventListener("click", () => {
        this.executeMultiSearch(pageSection, select);
      });
    }

    // Restaurer depuis l'URL
    this.restoreMultiSelection(select, badgesContainer, pageSection);
  }

  renderBadges(container, pageSection) {
    if (!container) return;

    container.innerHTML = "";

    this.selectedThematiques.forEach((name, id) => {
      const badge = document.createElement("span");
      badge.className = pageSection + "-filters__badge";
      badge.dataset.id = id;

      const text = document.createElement("span");
      text.className = pageSection + "-filters__badge-text";
      text.textContent = name;

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = pageSection + "-filters__badge-remove";
      removeBtn.setAttribute("aria-label", "Retirer " + name);
      removeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

      removeBtn.addEventListener("click", () => {
        this.selectedThematiques.delete(id);
        this.renderBadges(container, pageSection);
      });

      badge.appendChild(text);
      badge.appendChild(removeBtn);
      container.appendChild(badge);
    });
  }

  async executeMultiSearch(pageSection, select) {
    const ids = Array.from(this.selectedThematiques.keys());
    // Joindre par virgule (safe pour URL, converti en + côté PHP pour Views OR)
    const thematiqueParam = ids.join(",");

    await this.fetchResults(pageSection, thematiqueParam, select);
  }

  restoreMultiSelection(select, badgesContainer, pageSection) {
    const currentUrl = new URL(window.location.href);
    const urlThematique = currentUrl.searchParams.get("thematique");

    if (urlThematique) {
      // Peut contenir plusieurs IDs separes par virgule
      const ids = urlThematique.split(",");

      ids.forEach((id) => {
        const option = Array.from(select.options).find(
          (opt) => opt.value === id,
        );
        if (option) {
          this.selectedThematiques.set(id, option.text);
        }
      });

      this.renderBadges(badgesContainer, pageSection);
    }
  }

  // ─── FETCH AJAX PARTAGE ───────────────────────────────────────

  /**
   * Construire l'URL sans encodage parasite de searchParams
   */
  buildUrl(thematiqueParam) {
    const base = window.location.origin + window.location.pathname;
    if (thematiqueParam) {
      return base + "?thematique=" + thematiqueParam;
    }
    return base;
  }

  async fetchResults(pageSection, thematiqueParam, select) {
    select.classList.add("is-loading");
    const resultsContainer = document.getElementById(pageSection + "-results");
    if (resultsContainer) {
      resultsContainer.classList.add("is-loading");
      resultsContainer.style.opacity = "0.5";
    }

    try {
      const fetchUrl = this.buildUrl(thematiqueParam);

      const response = await fetch(fetchUrl, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error("Erreur de chargement");
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newResultsContainer = doc.getElementById(pageSection + "-results");

      if (newResultsContainer && resultsContainer) {
        resultsContainer.innerHTML = newResultsContainer.innerHTML;
      }

      window.history.pushState({}, "", fetchUrl);

      this.announceResults(pageSection, resultsContainer);
    } catch (error) {
      console.error("Erreur lors du filtrage:", error);
      this.showError(pageSection);
    } finally {
      select.classList.remove("is-loading");
      if (resultsContainer) {
        resultsContainer.classList.remove("is-loading");
        resultsContainer.style.opacity = "1";
      }
    }
  }

  announceResults(pageSection, container) {
    const statusElement = document.getElementById(
      pageSection + "-results-status",
    );
    if (statusElement) {
      const cards = container
        ? container.querySelectorAll("article, .views-row")
        : [];
      const count = cards.length;
      statusElement.textContent = `${count} resultat${count > 1 ? "s" : ""} trouve${count > 1 ? "s" : ""}`;
    }
  }

  showError(pageSection) {
    const statusElement = document.getElementById(
      pageSection + "-results-status",
    );
    if (statusElement) {
      statusElement.textContent =
        "Une erreur est survenue lors du chargement. Veuillez reessayer.";
    }
  }
}

// Initialisation automatique au chargement du DOM
document.addEventListener("DOMContentLoaded", () => {
  new ThematiqueFilter();
});
